/**
 * 首次落地归因：搜索引擎 / AI / 其它来源写入本地缓存，请求时放到 header。
 * 只记第一次，后续站内跳转不覆盖。
 */

export const LANDING_SOURCE_KEY = 'jufair_landing_source'

export const LandingHttpHeaders = {
  TYPE: 'landingType',
  ENGINE: 'landingEngine',
  KEYWORD: 'landingKeyword',
  ENTRY: 'landingEntry',
  REFERRER: 'landingReferrer',
} as const

export type LandingSourceType = 'search_engine' | 'ai' | 'social' | 'direct' | 'other'

export interface LandingSource {
  type: LandingSourceType
  /** 搜索引擎或 AI 产品标识，如 baidu、chatgpt */
  engine: string
  keyword: string
  entryUrl: string
  referrer: string
}

const SEARCH_ENGINES: { id: string; hosts: string[]; params: string[] }[] = [
  { id: 'baidu', hosts: ['baidu.com'], params: ['wd', 'word'] },
  { id: 'google', hosts: ['google.'] , params: ['q'] },
  { id: 'bing', hosts: ['bing.com'], params: ['q'] },
  { id: 'so', hosts: ['so.com'], params: ['q'] },
  { id: 'sogou', hosts: ['sogou.com', 'sougou.com'], params: ['query', 'keyword', 'word'] },
  { id: 'shenma', hosts: ['sm.cn'], params: ['q'] },
  { id: 'yahoo', hosts: ['yahoo.com'], params: ['p', 'q'] },
  { id: 'yandex', hosts: ['yandex.'], params: ['text', 'q'] },
  { id: 'duckduckgo', hosts: ['duckduckgo.com'], params: ['q'] },
]

const AI_SOURCES: { id: string; hosts: string[] }[] = [
  { id: 'chatgpt', hosts: ['chatgpt.com', 'chat.openai.com'] },
  { id: 'claude', hosts: ['claude.ai'] },
  { id: 'gemini', hosts: ['gemini.google.com', 'bard.google.com'] },
  { id: 'perplexity', hosts: ['perplexity.ai'] },
  { id: 'copilot', hosts: ['copilot.microsoft.com'] },
  { id: 'deepseek', hosts: ['deepseek.com'] },
  { id: 'kimi', hosts: ['kimi.com', 'moonshot.cn'] },
  { id: 'doubao', hosts: ['doubao.com'] },
  { id: 'yuanbao', hosts: ['yuanbao.tencent.com'] },
  { id: 'qwen', hosts: ['tongyi.aliyun.com', 'qianwen.aliyun.com', 'qwen.ai'] },
  { id: 'wenxin', hosts: ['yiyan.baidu.com'] },
  { id: 'poe', hosts: ['poe.com'] },
  { id: 'grok', hosts: ['grok.com', 'x.ai'] },
  { id: 'meta_ai', hosts: ['meta.ai'] },
  { id: 'you', hosts: ['you.com'] },
]

const AI_UTM = new Set(AI_SOURCES.map(item => item.id))

function hostOf(raw: string): string {
  try {
    return new URL(raw).hostname.toLowerCase()
  } catch {
    return ''
  }
}

function queryOf(raw: string): URLSearchParams | null {
  try {
    return new URL(raw).searchParams
  } catch {
    return null
  }
}

function pickParam(params: URLSearchParams | null, keys: string[]): string {
  if (!params) return ''
  for (const key of keys) {
    const value = params.get(key)
    if (value && value.trim()) return value.trim().slice(0, 200)
  }
  return ''
}

function matchHost(host: string, needles: string[]): boolean {
  return needles.some(item => host.includes(item))
}

/** 本站及子域不作为外部来源 */
function isOwnHost(host: string): boolean {
  return host === 'jufair.com' || host.endsWith('.jufair.com')
}

/** 无法归类时用 referrer 主机名；本站域名返回空 */
function externalHost(host: string): string {
  if (!host || isOwnHost(host)) return ''
  return host.slice(0, 64)
}

export function parseLandingSource(referrer: string, entryUrl: string): LandingSource {
  const refHost = hostOf(referrer)
  const entryParams = queryOf(entryUrl)
  const utm = (entryParams?.get('utm_source') || entryParams?.get('utm_medium') || '').toLowerCase()

  for (const ai of AI_SOURCES) {
    if (matchHost(refHost, ai.hosts) || utm.includes(ai.id)) {
      return { type: 'ai', engine: ai.id, keyword: '', entryUrl, referrer }
    }
  }
  if (utm && AI_UTM.has(utm)) {
    return { type: 'ai', engine: utm, keyword: '', entryUrl, referrer }
  }

  for (const engine of SEARCH_ENGINES) {
    if (matchHost(refHost, engine.hosts)) {
      return {
        type: 'search_engine',
        engine: engine.id,
        keyword: pickParam(queryOf(referrer), engine.params),
        entryUrl,
        referrer,
      }
    }
  }

  const host = externalHost(refHost)
  if (!host) {
    return { type: 'direct', engine: '', keyword: '', entryUrl, referrer: isOwnHost(refHost) ? '' : referrer }
  }
  if (/weibo\.com|weixin|wechat|qq\.com|douyin\.com|facebook\.com|twitter\.com|linkedin\.com|xiaohongshu\.com/.test(host)) {
    return { type: 'social', engine: host, keyword: '', entryUrl, referrer }
  }
  return { type: 'other', engine: host, keyword: '', entryUrl, referrer }
}

function readStored(): LandingSource | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(LANDING_SOURCE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as LandingSource
    if (!data || !data.entryUrl) return null
    return data
  } catch {
    return null
  }
}

/**
 * 仅在还没有来源缓存时写入。
 * jufair.com 及子域是站内跳转，不写入、也不覆盖已有来源。
 */
export function captureLandingSource(): LandingSource | null {
  if (typeof window === 'undefined') return null
  const existing = readStored()
  if (existing) return existing
  const referrer = document.referrer || ''
  if (isOwnHost(hostOf(referrer))) return null
  const entryUrl = window.location.href.slice(0, 1000)
  const source = parseLandingSource(referrer, entryUrl)
  if (isOwnHost(source.engine) || isOwnHost(hostOf(source.referrer))) return null
  try {
    localStorage.setItem(LANDING_SOURCE_KEY, JSON.stringify(source))
  } catch {
    /* ignore quota */
  }
  return source
}

export function getLandingSource(): LandingSource | null {
  return readStored()
}

export function appendLandingHeaders(headers: { set: (k: string, v: string) => void } | Record<string, string>) {
  const source = getLandingSource()
  if (!source) return
  const pairs: [string, string][] = [
    [LandingHttpHeaders.TYPE, source.type],
    [LandingHttpHeaders.ENGINE, source.engine],
    [LandingHttpHeaders.KEYWORD, source.keyword],
    [LandingHttpHeaders.ENTRY, source.entryUrl.slice(0, 500)],
    [LandingHttpHeaders.REFERRER, source.referrer.slice(0, 500)],
  ]
  const set = (key: string, value: string) => {
    if (!value) return
    if ('set' in headers && typeof headers.set === 'function') {
      headers.set(key, value)
    } else {
      ;(headers as Record<string, string>)[key] = value
    }
  }
  for (const [key, value] of pairs) set(key, value)
}
